<template>
  <v-menu
    v-model="menu"
    max-width="300px"
    max-height="50%"
    offset-y
    left
    top
    :close-on-content-click="false"
    :disabled="disabled"
  >
    <v-btn
      slot="activator"
      color="success"
      :disabled="disabled || willOpportunityOpen"
    >
      {{ opportunity.type == "event" ? "RSVP" : "Apply" }}
    </v-btn>
    <v-card>
      <v-card-text>
        <v-layout
          row
          align-start
          justify-start
          v-if="willOpportunityOpen && isEligible"
        >
          <div class="pt-1">
            <v-avatar size="30" :color="themes.candidate.light.success">
              <v-icon dark small>mdi-calendar</v-icon>
            </v-avatar>
          </div>
          <div class="px-2">
            Congrats! You are eligible to apply for this opportunity.
            <br />Applications open in
            <span class="font-weight-bold">{{ timeToOpeningDate }}</span>
            ({{ new Date(this.opportunity.opensAt) | date("d MMM yyy") }})
          </div>
        </v-layout>

        <div v-else-if="!isEligible && !isReferred">
          <v-layout row align-start justify-start>
            <div class="pt-1">
              <v-avatar size="30" :color="themes.candidate.light.error">
                <v-icon dark small>mdi-thumb-down</v-icon>
              </v-avatar>
            </div>
            <div class="px-2">
              Based on the information you provided it does not look like you
              qualify for this opportunity. Please see the minimum requirement
              information.
            </div>
          </v-layout>
        </div>

        <div
          v-else-if="(isEligible && isOpportunityOpen) || isReferred"
          class="px-2"
        >
          <v-form v-model="valid" v-if="opportunity.locations" ref="form">
            <v-select
              ref="select"
              v-model="locations"
              label="Opportunity Locations"
              :color="themes.candidate.light.input"
              :items="opportunity.locations"
              :rules="rules.location"
              hint="Choose your preferred locations"
              item-text="address"
              item-value="id"
              persistent-hint
              return-object
              multiple
              :menu-props="{ offsetY: true }"
            />
          </v-form>
          <v-checkbox
            v-if="!user.termsAcceptedAt"
            label="I consent to Leaply using my Personal Information as described in Leaply's Privacy Policy."
            v-model="termsAccepted"
            :color="themes.candidate.light.input"
            :rules="rules.required"
          />
          <a
            href="https://about.leap.ly/assets/privacy_policy.pdf"
            target="_blank"
            >View Privacy Policy</a
          >
          <v-checkbox
            label="I confirm that the Personal Information I have provided in the application is complete, accurate and up to date."
            v-model="dataAccurate"
            :color="themes.candidate.light.input"
            :rules="rules.required"
          />
          <v-btn
            color="success"
            :loading="applying"
            :disabled="!valid || !dataAccurate || !termsAccepted || applying"
            @click.stop="apply"
            class="px-4"
            block
          >
            {{ opportunity.type == "event" ? "Submit" : "Submit Application" }}
          </v-btn>
        </div>

        <v-layout row align-start justify-start v-else-if="isOpportunityClosed">
          <div>
            <v-avatar size="30" :color="themes.candidate.light.muted">
              <v-icon dark small>mdi-calendar</v-icon>
            </v-avatar>
          </div>
          <div class="pt-1 px-2">
            Applications for this opportunity are closed.
          </div>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
// Helpers
import _ from "lodash";
import { mapGetters } from "vuex";

// Mixins
import { OpportunityDates } from "@/mixins/OpportunityDates.js";

// Set the last time the terms were changed here to reprompt acceptance
const mostRecentTerms = new Date(2021, 5, 30); // July 2021

export default {
  mixins: [OpportunityDates],

  created() {
    this.termsAccepted = new Date(this.user.termsAcceptedAt) > mostRecentTerms;
    this.$store
      .dispatch("candidate/eligibilities/checkEligibility", this.opportunity)
      .finally(() => {
        this.loading = false;
        if (_.get(this, "$refs.form")) this.$refs.form.validate();
        if (_.get(this, "$refs.select")) this.$refs.select.focus();
      });
  },

  props: {
    opportunity: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      applying: false,
      loading: true,
      locations: [],
      termsAccepted: false,
      dataAccurate: false,
      menu: false,
      rules: {
        location: [(v) => v.length > 0 || "Must select location(s)"],
        required: [(v) => v],
      },
      valid: true,
    };
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
    }),

    eligibility() {
      return this.$store.getters["candidate/eligibilities/forOpportunity"](
        this.opportunity.id
      );
    },

    isEligible() {
      return this.eligibility && this.eligibility.eligible;
    },

    isReferred() {
      return this.eligibility && !!this.eligibility.referredAt;
    },
  },

  methods: {
    apply() {
      if (this.$refs["form"].validate()) {
        this.applying = true;

        if (this.user.termsAcceptedAt < mostRecentTerms) {
          this.$http
            .post("candidates/accept_terms")
            .then(() => this.$store.dispatch("auth/refresh"));
        }

        this.$store
          .dispatch("candidate/opportunities/apply", {
            opportunity: this.opportunity,
            locations: this.locations,
          })
          .then(
            ({ data }) => {
              // Success
              this.$store.dispatch("snackbarSuccess", "Application Submitted");
              this.$store.dispatch("candidate/applications/fetch", data.id);
              this.$emit("done");
              this.applying = false;
            },
            // Failure
            () => {
              this.$store.dispatch("snackbarError", "Error Occured");
              this.applying = false;
            }
          );
      }
    },
  },
};
</script>
